// frontend/src/pages/ProjectExport.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "@/components/ui/Button";
import { useProjectFormStore } from "@/store/project-form-provider";
import { useProjectResults } from "@/utils/use-project-results";
import { downloadFimoXlsx, downloadFimoPdf, defaultFileDatePrefix } from "@/utils/export-fimo";
import { downloadSummaryPdf, downloadSummaryXlsx } from "@/utils/export-summary";
import { showToast } from "@/utils/toast";

type ExportType = "summary" | "fimo";
type ExportFormat = "pdf" | "xlsx";

export default function ProjectExport() {
  const { id } = useParams();
  const { results, resultsLoading } = useProjectFormStore(); // provider cache
  const { results: calcResults } = useProjectResults(id);    // ensure fresh data on direct open
  const { project } = useProjectFormStore();
  const effectiveResults = results ?? calcResults ?? null;

  const { t } = useTranslation();

  const resolveProjectName = (): string => {
    const r: any = effectiveResults || {};
    const p = project as any;
    return (
      p?.name ||
      r?.project?.name ||
      r?.meta?.project_name ||
      r?.key_metrics?.[0]?.["Project Name"] ||
      r?.key_metrics?.[0]?.["project_name"] ||
      (id ? `Project ${id}` : "Unnamed Project")
    );
  };

  const [exportType, setExportType] = useState<ExportType>("summary");
  const [exportFormat, setExportFormat] = useState<ExportFormat>("pdf");

  // Project Summary section selection
  const [includeKeyFacts, setIncludeKeyFacts] = useState<boolean>(true);
  const [includeFinancing, setIncludeFinancing] = useState<boolean>(true);
  const [includeCashFlow, setIncludeCashFlow] = useState<boolean>(true);

  // FiMo PDF orientation (landscape fits wide year tables better)
  const [fimoPdfOrientation, setFimoPdfOrientation] = useState<"portrait" | "landscape">("landscape");

  const [filename, setFilename] = useState<string>("");

  const defaultFilename = useMemo(() => {
    const datePrefix = defaultFileDatePrefix();
    const name = String(resolveProjectName()).replace(/[\\/:*?\"<>|]+/g, " ").trim();
    const typeSlug = exportType === "fimo" ? t("project.export.financial_model_data") : t("project.export.project_summary");
    return `${datePrefix} ELANI Energy - ${name} - ${typeSlug}`;
  }, [exportType, effectiveResults, project, t]);

  useEffect(() => {
    setFilename(defaultFilename);
  }, [defaultFilename]);

  const onDownload = async () => {
    if (!effectiveResults) {
      showToast?.(t("project.export.no_results"), "warning");
      return;
    }
    try {
      if (exportType === "fimo") {
        if (exportFormat === "xlsx") {
          await downloadFimoXlsx(effectiveResults, project, `${filename}.xlsx`);
        } else {
          await downloadFimoPdf(effectiveResults, project, `${filename}.pdf`, { orientation: fimoPdfOrientation });
        }
      } else if (exportType === "summary") {
        if (exportFormat === "xlsx") {
          await downloadSummaryXlsx(effectiveResults, project, `${filename}.xlsx`, {
            includeKeyFacts,
            includeFinancing,
            includeCashFlow,
          });
        } else {
          await downloadSummaryPdf(effectiveResults, project, `${filename}.pdf`, {
            includeKeyFacts,
            includeFinancing,
            includeCashFlow,
          });
        }
      }
      showToast?.(t("project.export.download_prepared"), "success");
    } catch (err: any) {
      const msg = err?.message || t("project.export.download_failed");
      showToast?.(msg, "error");
    }
  };

  return (
    <div className="overflow-x-auto max-w-6xl text-xs">
      <div className="bg-white rounded-xl shadow-sm border border-border p-6">

        <div className="text-xs text-gray-800 space-y-4">
          {/* Single-column, compact form */}
          <div className="flex flex-col gap-4 max-w-xl">
            <div className="space-y-1">
              <label className="block font-medium">{t("project.export.type")}</label>
              <select
                value={exportType}
                onChange={(e) => setExportType(e.target.value as ExportType)}
                className="border rounded p-2 text-xs bg-white w-72"
              >
                <option value="summary">{t("project.export.project_summary")}</option>
                <option value="fimo">{t("project.export.financial_model_data")}</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="block font-medium">{t("project.export.format")}</label>
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                className="border rounded p-2 text-xs bg-white w-72"
              >
                <option value="pdf">{t("project.export.pdf")}</option>
                <option value="xlsx">{t("project.export.xlsx")}</option>
              </select>
            </div>

            {exportType === "summary" && (
              <div className="space-y-2">
                <div className="font-medium">{t("project.export.sections")}</div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeKeyFacts}
                    onChange={(e) => setIncludeKeyFacts(e.target.checked)}
                    className="accent-elani-orange-300"
                  />
                  <span>{t("project.export.key_facts")}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeCashFlow}
                    onChange={(e) => setIncludeCashFlow(e.target.checked)}
                    className="accent-elani-orange-300"
                  />
                  <span>{t("project.export.cash_flow")}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeFinancing}
                    onChange={(e) => setIncludeFinancing(e.target.checked)}
                    className="accent-elani-orange-300"
                  />
                  <span>{t("project.export.debt_schedule")}</span>
                </label>
              </div>
            )}

            {/*exportType === "fimo" && exportFormat === "pdf && (
              <div className="space-y-1">
                <label className="block font-medium">{t("project.export.pdf_orientation")}</label>
                <select
                  value={fimoPdfOrientation}
                  onChange={(e) => setFimoPdfOrientation(e.target.value as any)}
                  className="border rounded p-2 text-xs bg-white w-72"
                >
                  <option value="landscape">{t("project.export.landscape")}</option>
                  <option value="portrait">{t("project.export.portrait")}</option>
                </select>
              </div>
            )*/}

            {/* Filename last, above the button */}
            <div className="space-y-1">
              <label className="block font-medium">{t("project.export.file_name")}</label>
              <input
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                className="border rounded p-2 text-xs w-[32rem]"
                placeholder={t("project.export.file_name_placeholder")}
              />
            </div>

            <div className="pt-2">
              <Button
                variant="orange"
                loading={resultsLoading}
                loadingText={t("common.preparing")}
                className="font-semibold"
                disabled={!effectiveResults || resultsLoading}
                onClick={onDownload}
              >
                {t("actions.download")}
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}